import { Injectable, OnDestroy } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';

import { Subscription } from 'rxjs';
import { GenericUtils } from '@unpispas/upp-defs';

import { adhocService } from '@unpispas/upp-base';
import { stateService } from '@unpispas/upp-base';
import { languageService } from '@unpispas/upp-base';
import { toastService } from '@unpispas/upp-base';
import { dataService } from './data';

import { Session } from './model/objects/session';
import { Place } from './model/objects/place';

/********************************/
/* LOCAL STORAGE DEFINITIONS    */
/********************************/

/** Interface representing stored login information. */
interface StoredLogin {
    /** Unique device identifier. */
    deviceid: string;
    /** Current session ID. */
    session: string;
    /** Username associated with the session. */
    username: string;
};

/** Interface representing session token details. */
interface SessionToken {
    /** Session identifier. */
    session: string | null;
    /** Place identifier. */
    placeid: string | null;
};

/**
 * Injectable service to handle login and authentication tasks.
 * Provides methods for local storage manipulation, session updates,
 * user authentication, and guest access.
 */
@Injectable({
    providedIn: 'root'
})
export class loginService implements OnDestroy {
    /** Subscription to session change events. */
    private _onsession_subscription: Subscription | null = null; 
    /** Subscription to place change events. */
    private _onplace_subscription: Subscription | null = null;
    /** Subscription to session released event. */
    private _onrelease_subscription: Subscription | null = null;


    /**
     * Initializes the service and subscribes to session and place change events.
     * @param lang - Language service for translations.
     * @param state - State management service.
     * @param adhoc - Service for HTTP requests.
     * @param toast - Service for showing toast notifications.
     * @param data - Data service for session and place updates.
     * @param router - Angular router for navigation.
     * @param route - Current activated route.
     */    
    constructor(private lang: languageService, private state: stateService, private adhoc: adhocService, private toast: toastService, private data: dataService, private router: Router, private route: ActivatedRoute){
        this._onsession_subscription = this.data.OnSessionChanged.subscribe(
        (data: Session) => {
            let _token: SessionToken | null = this.GetStoredToken();
            if (_token){
                _token.session = data.id;
                this.UdtStoredToken(_token);
            }
            else {
                if (!this.state.session || !this.state.device){
                    throw new Error ("[LOGIN] Session could not been established")
                }

                this.SetStoredToken(this._Token(this.state.session, this.state.device)).then(
                (res) => {
                    _token = {
                        session: this.state.session,
                        placeid: null
                    };
    
                    this.UdtStoredToken(_token);
                    if (!res){
                        console.warn("[LOGIN] Session token URL parameter could not be updated");
                    }
                })
            }
        })
    
        this._onplace_subscription = this.data.OnPlaceChanged.subscribe(
        (place: Place | null) => {
            const _token: SessionToken | null = this.GetStoredToken();
            if (_token){
                _token.placeid = place ? place.objid : null;
                this.UdtStoredToken(_token);
            }
        })   
        
        this._onrelease_subscription = this.data.OnSessionReleased.subscribe(
        async (expired) => {
            await this.DoLogout(expired);
        });
    }

    /**
     * Unsubscribes from all active subscriptions upon destruction.
     */
    ngOnDestroy(): void {
        if (this._onsession_subscription){
            this._onsession_subscription.unsubscribe();
            this._onsession_subscription = null;
        }

        if (this._onplace_subscription){
            this._onplace_subscription.unsubscribe();
            this._onplace_subscription = null;
        }

        if (this._onrelease_subscription){
            this._onrelease_subscription.unsubscribe();
            this._onrelease_subscription = null;
        }
    }

    /**
     * Logs error messages for failed HTTP requests.
     * @param url - The URL of the request.
     * @param error - Error details.
     * @returns `false` indicating failure.
     */    
    private _throwerror(url: string, error: any): boolean {
        if (error && error.status) {
            console.error("[REQUEST] Error [" + error.status + "] in http request '" + url + "'");
        } 
        else {
            console.error("[REQUEST] Generic error in http request '" + url + "'");
        }            

        return false;
    }

    /**
     * `ApplicationError::Exists` from the server — login refused because another
     * live SESSION already holds the same device id (`grant_session`).
     */
    static readonly ErrorExists = 8;

    /** `errorcode` from the last `DoLogin` response; `null` before the first attempt. */
    private _lasterror: number | null = null;

    /**
     * `errorcode` of the last `DoLogin` response (`null` if not attempted yet).
     * Cleared at the start of each `DoLogin`. Callers use this to distinguish
     * refusal reasons (e.g. {@link ErrorExists}) after a failed login.
     */
    get LastError(): number | null {
        return this._lasterror;
    }

    /********************************/
    /* LOCAL STORAGE                */
    /********************************/

    /** Key for storing login data (keep session alive) in local storage. */
    private _storedloginkey = 'upp-stored-logindata';

    /**
     * Retrieves stored login data for a specific device.
     * @param device - Device identifier.
     * @returns The stored login data or `null` if not found.
     */    
    private GetStoredLogin(device: string): StoredLogin | null{
        const _stored = localStorage.getItem(this._storedloginkey);
        if (_stored){
            const _login = JSON.parse(_stored) as StoredLogin;
            if (_login.deviceid == device){
                return _login;
            }
        }
        return null;
    }

    /**
     * Stores login data in local storage.
     * @param device - Device identifier.
     * @param session - Session identifier.
     * @param username - Username associated with the session.
     */    
    private SetStoredLogin(device: string, session: string, username: string): void{
        const _data: StoredLogin = {
            deviceid: device,
            session: session,
            username: username
        };

        localStorage.setItem(this._storedloginkey, JSON.stringify(_data));
    }
    
    /**
     * Removes stored login data from local storage.
     */    
    private DelStoredLogin(): void {
        localStorage.removeItem(this._storedloginkey);
    }

    /** Key for storing token data (recover on page refresh) in local storage. */
    private _storedtokenkey: string | null = null;

    /**
     * Sets a session token and updates the URL query parameters.
     * @param token - Token to be stored.
     * @returns A promise resolving to `true` if the token was successfully set.
     */    
    private async SetStoredToken(token: string | null): Promise <boolean> {
        if (this._storedtokenkey){
            console.warn("[LOGIN] Updating session token key")
        }

        this._storedtokenkey = token;

        // https://stackoverflow.com/questions/46213737/angular-append-query-parameters-to-url
        return await this.router.navigate([], {
            relativeTo: this.route,
            queryParams: {
              token: this._storedtokenkey
            },

            queryParamsHandling: 'merge',   // preserve the existing query params in the route
            skipLocationChange: false       // update the url params
          });
    }

    /**
     * Retrieves the stored session token from local storage.
     * @returns The stored session token or `null` if not found.
     */    
    private GetStoredToken(): SessionToken | null {
        if (!this._storedtokenkey){
            return null;    // no token has been set
        }

        const _stored = localStorage.getItem(this._storedtokenkey);
        if (_stored){
            return JSON.parse(_stored) as SessionToken;
        }

        return null;
    }

    /**
     * Updates the stored session token in local storage.
     * @param token - The token to be updated or `null` to remove the token.
     */    
    private UdtStoredToken(token: SessionToken | null): void {
        if (!this._storedtokenkey){
            return;    // no token has been set
        }

        if (token){
            localStorage.setItem(this._storedtokenkey, JSON.stringify(token))
        }
        else {
            localStorage.removeItem(this._storedtokenkey);
        }
    }

    /**
     * Deletes the stored session token and clears the query parameters.
     * @returns A promise resolving to `true` if the token was successfully removed.
     */    
    private async DelStoredToken(): Promise <boolean> {
        if (!this._storedtokenkey){
            return false;    // no token has been set
        }

        localStorage.removeItem(this._storedtokenkey);
        this._storedtokenkey = null;

        return await this.router.navigate([], {
            relativeTo: this.route,
            queryParams: {
              token: null
            },

            queryParamsHandling: 'merge',   // preserve the existing query params in the route
            skipLocationChange: false       // update the url params
        });
    }

    /******************************/
    /* LOGOUT METHODS             */
    /******************************/    

    private _doLogoutUrl = 'login/logout';

    /**
     * Performs the logout operation, stopping data services and clearing stored data.
     * @param expired - Indicates if the session expired due to inactivity.
     * @returns A promise resolving to `true` if logout was successful.
     */    
    async DoLogout(expired = false) : Promise <boolean> {
        if (this.state.session == null){
            return true;     // already released
        }

        await this.DelStoredToken();
        let _serverLogout = false;
        try {
            await this.adhoc.DoRequest(this._doLogoutUrl, null, null);
            _serverLogout = true;
        }
        catch(error: any){
            this._throwerror(this._doLogoutUrl, error);
        }

        if (!expired){  // do not remove stored information if expired due to inactivity
            this.DelStoredLogin();
        }
        else if (_serverLogout){  // try to perform stored login to create new session
            await this.DoStored();
        }

        return this.data.Stop(expired);
    }

    /******************************/
    /* ADDITIONAL METHODS         */
    /******************************/    

    private _Token(session: string, device: string){
        return '_' + GenericUtils.CRC32(session + device).toString(16);
    }

    private _doPasswordUrl = 'login/password';
    private _doActivateUrl = 'login/resend';
    private _freeUserUrl = 'login/available';

    /**
     * Updates the user's password.
     * @param oldpassword - The user's current password.
     * @param newpassword - The new password to set.
     * @param singlecrc - Indicates if only a single CRC32 hash is used for encoding.
     * @returns A promise resolving to `true` if the password was successfully updated.
     */    
    async DoPassword(oldpassword: string, newpassword: string, singlecrc: boolean): Promise <boolean> {
        try {
            const _params = {
                old: this._passwordEncode(oldpassword, singlecrc),
                new: this._passwordEncode(newpassword)
            };
        
            const _data: any= await this.adhoc.DoRequest(this._doPasswordUrl, _params, null);

            const _success = (_data['errorcode'] == 0);
            if (_success) {
                await this.DelStoredLogin();
            }
            return _success;
        }
        catch(error: any){
            return this._throwerror(this._doPasswordUrl, error);
        }
    }

    /**
     * Sends an activation request for the specified username.
     * @param username - The username to activate.
     * @returns A promise resolving to `true` if activation was successful.
     */    
    async DoActivate(username: string): Promise <boolean> {
        try {
            return (((await this.adhoc.DoRequest(this._doActivateUrl, { username: username }, null)) as any)['errorcode'] == 0);
        }
        catch (error: any){
            return this._throwerror(this._doActivateUrl, error);
        }
    }

    /**
     * Checks the availability of a username.
     * @param username - The username to check.
     * @returns A promise resolving to `true` if the username is available.
     */    
    async Available(username: string): Promise <boolean> {
        try {
            return !!(await this.adhoc.DoRequest(this._freeUserUrl, { username: username }))
        }
        catch (error: any){
            return this._throwerror(this._freeUserUrl, error);
        }
    }

    /******************************/
    /* USER LOGIN METHODS         */
    /******************************/    

    private _doLoginUrl = 'login';
    private _doStoredUrl = 'login/stored';
    private _doRegisterUrl = 'login/register';
    private _doRecoverUrl = 'login/recover';
    private _doAccessUrl = 'guest/access'

    private _passwordEncode(_encode0: string, singlecrc = false){
        const _encode1 = GenericUtils.CRC32(_encode0).toString(16);
        const _encode2 = GenericUtils.CRC32(_encode1).toString(16);

        return singlecrc ? _encode1 : _encode2;
    }

    private async _waitForDevice(): Promise<string>{
        await this.data.Ready();

        if (!this.state.device){
            const _timeout = setTimeout(() => {
                throw new Error("[LOGIN] System status initialization failed (no device)!");
            }, 1000);

            await this.state.OnDevice;
            clearTimeout(_timeout);
        }

        if (!this.state.device){
            throw new Error("[LOGIN] device could not be retrieved after waiting.");
        }

        return this.state.device;
    }

    private async _waitForSession(): Promise<string>{
        if (!this.state.session){
            const _timeout = setTimeout(() => {
                throw new Error("[LOGIN] System status initialization failed (no session)!");
            }, 1000);

            await this.state.OnSession;
            clearTimeout(_timeout);
        }

        if (!this.state.session){
            throw new Error("[LOGIN] session could not be retrieved after waiting.");
        }

        return this.state.session;
    }

    /**
     * Performs user login with the provided credentials.
     * @param username - The username to log in.
     * @param password - The password for the username.
     * @param remember - Indicates if login data should be stored for future sessions.
     * @param singlecrc - Indicates if only a single CRC32 hash is used for encoding the password.
     * @returns A promise resolving to `true` if login was successful.
     */    
    async DoLogin(username: string, password: string, remember: boolean, singlecrc: boolean): Promise <boolean> {  
        await this.data.Ready();
        this._lasterror = null;

        let _data: any = null;
        try {
            const _password = this._passwordEncode(password, singlecrc);
            _data = await this.adhoc.DoRequest(this._doLoginUrl, null, { username: username, password: _password });

            this._lasterror = (_data['errorcode'] != null) ? Number(_data['errorcode']) : null;
            const _success = (this._lasterror === 0);
            if (_success){
                await this.data.Start(_data['session'], 'LOGIN');
                if (remember){
                    this.SetStoredLogin(await this._waitForDevice(), await this._waitForSession(), username);
                }
            }

            return _success;
        }
        catch (error: any) {
            return this._throwerror(this._doLoginUrl, error);
        }
    }

    /**
     * Uses a session token to authenticate the user.
     * @param _token - The token to authenticate.
     * @returns A promise resolving to `true` if the token was successfully validated.
     */    
    async DoToken(_token: string | null) : Promise <boolean> {
        await this.data.Ready();

        await this.SetStoredToken(_token);
        if (_token){
            const _data: SessionToken | null = await this.GetStoredToken();
            if (!_data){
                return false;
            }

            if (_data.session) {
                await this.data.Start(_data.session, 'LOGIN');
                if (_data.placeid){
                    const _place = this.data.store.GetByRef('PLACE', _data.placeid, null) as Place;
                    if (_place){
                        return await this.data.SetPlace(_place);
                    }
                }
                
                return true;
            }
        }
            
        return false;
    }

    /**
     * Attempts to log in using stored login data.
     * @returns A promise resolving to `true` if stored login data was successfully used.
     */    
    async DoStored(): Promise <boolean> {
        const _login: any = await this.GetStoredLogin(await this._waitForDevice());
        if (_login){
            const _success = await this.DoToken(this._Token(_login['session'], _login['deviceid']));
            if (_success){
                return true;    // token information available on the device (server connection not required)
            }
            else {
                try {
                    const _data: any = await this.adhoc.DoRequest(this._doStoredUrl, {
                        device: _login['deviceid'],
                        session: _login['session'],
                        username: _login['username'],
                    });
                    
                    const _success = (_data['errorcode'] == 0);
                    if (_success){
                        await this.data.Start(_data['session'], 'LOGIN');
                    }
                    else {
                        console.error("[SERVICE] Error [" +  _data['errorcode'] + "] in '" + this._doStoredUrl + "'");
                        this.toast.ShowAlert('danger', this.lang.tr('@service_request_error_' + _data['errorcode']));
                    }

                    return _success;
                }
                catch(error: any){
                    return this._throwerror(this._doStoredUrl, error);
                }
            }
        }

        return false;
    }

    /**
     * Sends a recovery request for the specified username.
     * @param username - The username to recover.
     * @returns A promise resolving to `true` if the recovery request was successful.
     */    
    async DoRecover(username: string): Promise <boolean> {
        try {
            await this.adhoc.DoRequest(this._doRecoverUrl, { username: username })
            return true;
        }
        catch(error: any){
            return this._throwerror(this._doRecoverUrl, error);
        }
    }

    /**
     * Registers a new user with the provided credentials.
     * @param username - The username to register.
     * @param password - The password for the new user.
     * @returns A promise resolving to `true` if registration was successful.
     */    
    async DoRegister(username: string, password: string): Promise<boolean> {
        await this.data.Ready();

        try {
            const _password = this._passwordEncode(password);
            const _data: any = await this.adhoc.DoRequest(this._doRegisterUrl, null, { username: username, password: _password });

            const _success = (_data['errorcode'] == 0);
            if (!_success){
                console.error("[SERVICE] Error [" +  _data['errorcode'] + "] in '" + this._doRegisterUrl + "'");
                this.toast.ShowAlert('danger', this.lang.tr('@service_request_error_' + _data['errorcode']));
            }

            return _success;
        } 
        catch(error: any) {
            return this._throwerror(this._doRegisterUrl, error);
        }
    }

    /******************************/
    /* GUEST ACCESS METHODS       */
    /******************************/    

   /**
     * Uses a token for guest access.
     * @param _token - The guest access token.
     * @returns A promise resolving to `true` if the token was successfully validated.
     */    
    private async _TokenAccess(_token: string): Promise <boolean> {
        await this.SetStoredToken(_token);
        const _data: any = await this.GetStoredToken();
        
        const _success = (_data && _data['session']);
        if (_success) {
            return await this.data.Start(_data['session'], 'GUEST');
        }
        return false;
    }

    /**
     * Uses table-based authentication for guest access.
     * @param _table - The table identifier.
     * @param _auth - The authentication key for the table.
     * @returns A promise resolving to `true` if authentication was successful.
     */    
    private async _TableAccess(_table: string, _auth: string): Promise <boolean> {
        try {
            const _data: any = await this.adhoc.DoRequest(this._doAccessUrl, null, { auth: _auth, table: _table })
            
            const _success = (_data['errorcode'] == 0);
            if (_success){
                return await this.data.Start(_data['session'], 'GUEST');
            }
            return false;
        }
        catch(error){
            return this._throwerror(this._doAccessUrl, error);
        }
    }

    /**
     * Performs guest access using a token, table, and authentication key.
     * Device id must already be on `state.device` from sync bootstrap (`DeviceId()`).
     * @param _table - The table identifier.
     * @param _auth - The authentication key for the table.
     * @param _token - The guest access token.
     * @returns A promise resolving to `true` if access was granted.
     */    
    async DoAccess(_table: string, _auth: string, _token: string): Promise <boolean>{
        await this._waitForDevice();

        let _success = false;

        if (!_success) {    // first try token access (if token is provided)
            if (_token){
                _success = await this._TokenAccess(_token);
            }
        }

        if (!_success){     // else try table access (will create a new session)
            _success = await this._TableAccess(_table, _auth);
        }

        return _success;
    }
}